/**
 * fileUtils.js
 * 
 * Utility functions for file operations and analysis.
 */

/**
 * Get language from file extension or path
 * @param {string} filePathOrLang - File path or language string
 * @returns {string} Language identifier
 */
export function getLanguageFromFile(filePathOrLang) {
  // If it's already a language identifier, return it
  if (!filePathOrLang.includes('.') && filePathOrLang.length < 15) {
    return normalizeLanguage(filePathOrLang);
  }
  
  // Extract extension from path
  const extension = filePathOrLang.split('.').pop().toLowerCase();
  
  // Map to Monaco editor language identifier
  const languageMap = {
    // JavaScript
    'js': 'javascript',
    'jsx': 'javascript',
    'mjs': 'javascript',
    
    // TypeScript
    'ts': 'typescript',
    'tsx': 'typescript',
    
    // Web
    'html': 'html',
    'css': 'css',
    'scss': 'scss',
    'sass': 'scss',
    'less': 'less',
    
    // Data formats
    'json': 'json',
    'yaml': 'yaml',
    'yml': 'yaml',
    'xml': 'xml',
    
    // Markdown and text
    'md': 'markdown',
    'txt': 'plaintext',
    
    // Other languages
    'py': 'python',
    'java': 'java',
    'c': 'c',
    'cpp': 'cpp',
    'h': 'cpp',
    'hpp': 'cpp',
    'cs': 'csharp',
    'go': 'go',
    'rb': 'ruby',
    'php': 'php',
    'rs': 'rust',
    'swift': 'swift',
    'sh': 'shell',
    'bash': 'shell',
    'sql': 'sql'
  };
  
  return languageMap[extension] || 'plaintext';
}

/**
 * Normalize language identifier
 * @param {string} language - Language string
 * @returns {string} Normalized language
 */
export function normalizeLanguage(language) {
  if (!language) return 'plaintext';
  
  const langLower = language.toLowerCase();
  
  // Map language variations to standard identifiers
  const langMap = {
    'javascript': 'javascript',
    'js': 'javascript',
    'typescript': 'typescript',
    'ts': 'typescript',
    'html': 'html',
    'css': 'css',
    'scss': 'scss',
    'json': 'json',
    'markdown': 'markdown',
    'md': 'markdown',
    'python': 'python',
    'py': 'python',
    'java': 'java',
    'csharp': 'csharp',
    'c#': 'csharp',
    'c': 'c',
    'cpp': 'cpp',
    'c++': 'cpp',
    'go': 'go',
    'ruby': 'ruby',
    'rb': 'ruby',
    'php': 'php',
    'rust': 'rust',
    'rs': 'rust',
    'shell': 'shell',
    'bash': 'shell',
    'sh': 'shell',
    'sql': 'sql',
    'text': 'plaintext',
    'txt': 'plaintext',
    'plaintext': 'plaintext'
  };
  
  return langMap[langLower] || langLower;
}

/**
 * Get file icon character based on language
 * @param {string} language - File language
 * @returns {string} Icon character
 */
export function getFileIcon(language) {
  const normalizedLang = normalizeLanguage(language);
  
  // Icon mapping (can be replaced with actual icons in components)
  const iconMap = {
    'javascript': 'JS',
    'typescript': 'TS',
    'html': 'HTML',
    'css': 'CSS',
    'scss': 'SCSS',
    'json': 'JSON',
    'markdown': 'MD',
    'python': 'PY',
    'java': 'JAVA',
    'csharp': 'C#',
    'c': 'C',
    'cpp': 'C++',
    'go': 'GO',
    'ruby': 'RB',
    'php': 'PHP',
    'rust': 'RS',
    'shell': 'SH',
    'sql': 'SQL',
    'plaintext': 'TXT'
  };
  
  return iconMap[normalizedLang] || '?';
}

/**
 * Get file name from path
 * @param {string} path - File path
 * @returns {string} File name
 */
export function getFileName(path) {
  return path.split('/').pop();
}

/**
 * Get directory from path
 * @param {string} path - File path
 * @returns {string} Directory path
 */
export function getDirectory(path) {
  return path.substring(0, path.lastIndexOf('/'));
}

/**
 * Create a directory structure from file paths
 * @param {Array} files - Array of file objects with paths
 * @returns {Object} Directory tree
 */
export function createDirectoryTree(files) {
  const root = { name: 'root', type: 'directory', children: {} };
  
  for (const file of files) {
    const pathParts = file.path.split('/');
    let currentLevel = root.children;
    
    // Process directories
    for (let i = 0; i < pathParts.length - 1; i++) {
      const part = pathParts[i];
      
      if (!part) continue; // Skip empty parts
      
      // Create directory if it doesn't exist
      if (!currentLevel[part]) {
        currentLevel[part] = { 
          name: part, 
          type: 'directory', 
          children: {} 
        };
      }
      
      currentLevel = currentLevel[part].children;
    }
    
    // Add file
    const fileName = pathParts[pathParts.length - 1];
    currentLevel[fileName] = { 
      name: fileName, 
      type: 'file', 
      id: file.id,
      language: file.language,
      path: file.path
    };
  }
  
  return root;
}

/**
 * Sort directory tree for display
 * @param {Object} tree - Directory tree
 * @returns {Array} Sorted tree items
 */
export function sortDirectoryTree(tree) {
  // Convert object to array
  const entries = Object.entries(tree).map(([name, node]) => ({
    ...node
  }));
  
  // Sort entries: directories first, then alphabetically
  entries.sort((a, b) => {
    if (a.type === 'directory' && b.type === 'file') return -1;
    if (a.type === 'file' && b.type === 'directory') return 1;
    return a.name.localeCompare(b.name);
  });
  
  // Recursively sort children
  for (const entry of entries) {
    if (entry.type === 'directory' && entry.children) {
      entry.children = sortDirectoryTree(entry.children);
    }
  }
  
  return entries;
}

/**
 * Generate a file path from components
 * @param {string} directory - Directory path
 * @param {string} name - File name
 * @param {string} extension - File extension
 * @returns {string} File path
 */
export function generateFilePath(directory, name, extension) {
  // Normalize directory
  const dir = directory ? (directory.endsWith('/') ? directory : directory + '/') : '';
  
  // Add extension if not already included
  const fileName = name.includes('.') ? name : `${name}.${extension}`;
  
  return `${dir}${fileName}`;
}

/**
 * Check if a file path exists in an array of files
 * @param {Array} files - Array of file objects with paths
 * @param {string} path - Path to check
 * @returns {boolean} True if exists
 */
export function filePathExists(files, path) {
  return files.some(file => file.path === path);
}

/**
 * Generate a unique file path
 * @param {Array} files - Array of file objects with paths
 * @param {string} path - Desired path
 * @returns {string} Unique path
 */
export function generateUniqueFilePath(files, path) {
  if (!filePathExists(files, path)) {
    return path;
  }
  
  // Split path into directory, name, and extension
  const lastSlash = path.lastIndexOf('/');
  const directory = lastSlash >= 0 ? path.substring(0, lastSlash + 1) : '';
  const nameWithExt = lastSlash >= 0 ? path.substring(lastSlash + 1) : path;
  
  const lastDot = nameWithExt.lastIndexOf('.');
  const name = lastDot >= 0 ? nameWithExt.substring(0, lastDot) : nameWithExt;
  const extension = lastDot >= 0 ? nameWithExt.substring(lastDot + 1) : '';
  
  // Try adding numbers until we find a unique path
  let counter = 1;
  let newPath;
  
  do {
    newPath = `${directory}${name}_${counter}${extension ? '.' + extension : ''}`;
    counter++;
  } while (filePathExists(files, newPath));
  
  return newPath;
}
